/**
 * Which contract directions an EAN's Atrias service components actually support.
 *
 * Lifted VERBATIM out of `AddService.tsx::getIntentValidation` so the wizard, the
 * step-2 direction radios and `validateExistingEanContracts` all answer this question
 * from ONE place. Parallel copies of this derivation are exactly how the
 * `hasAllThreeConfigs` / `getIntentValidation` disagreement (D1) came about — do not
 * re-inline it.
 *
 * Vocabulary (richer than the `SC_COMMINJ + SC_COMMOFF + SC_OFFINJE` triple that
 * `hasAllThreeConfigs` tests for):
 *   - injection: `SC_COMMINJ` OR `SC_INJECTI`
 *   - offtake:   `SC_COMMOFF` OR `SC_OFFTAKE`
 *   - both:      `SC_OFFINJE`
 *
 * `onlyOffinje` means the point can ONLY be contracted as a combined offtake+injection
 * service — a single direction on its own is impossible.
 */
export interface ServiceCapabilities {
  supportsInjection: boolean;
  supportsOfftake: boolean;
  onlyOffinje: boolean;
}

export const deriveServiceCapabilities = (
  sdpAtriasConfig?: string[],
  serviceComponent?: string
): ServiceCapabilities => {
  let supportsInjection = false;
  let supportsOfftake = false;
  let onlyOffinje = false;

  // First check sdpAtriasConfig
  if (sdpAtriasConfig && sdpAtriasConfig.length > 0) {
    // Extract only service component codes, ignore capacity codes
    const serviceCodes = sdpAtriasConfig.flatMap((config) =>
      config
        .split(",")
        .map((code) => code.trim())
        .filter((code) => code.startsWith("SC_")) // Only keep service component codes
    );

    // Remove duplicates
    const uniqueServiceCodes = [...new Set(serviceCodes)];

    // Check if SC_OFFINJE is the only service component code present
    onlyOffinje = uniqueServiceCodes.length === 1 && uniqueServiceCodes[0] === "SC_OFFINJE";

    // Check each service component code
    uniqueServiceCodes.forEach((code) => {
      // Injection codes
      if (code === "SC_COMMINJ" || code === "SC_INJECTI") {
        supportsInjection = true;
      }
      // Offtake codes
      if (code === "SC_COMMOFF" || code === "SC_OFFTAKE") {
        supportsOfftake = true;
      }
      // Both directions code
      if (code === "SC_OFFINJE") {
        supportsInjection = true;
        supportsOfftake = true;
      }
    });
  }
  // Fall back to serviceComponent if sdpAtriasConfig is empty
  else if (serviceComponent) {
    supportsInjection = serviceComponent.includes("SC_INJECTI");
    supportsOfftake = serviceComponent.includes("SC_OFFTAKE");
  }

  return { supportsInjection, supportsOfftake, onlyOffinje };
};

/**
 * True when the point's service components allow each direction to be contracted on its
 * own — i.e. a single-direction Customer Switch is structurally possible. `onlyOffinje`
 * points fail this: they can only ever be contracted as a combined pair.
 */
export const supportsIndependentDirections = (caps: ServiceCapabilities): boolean =>
  caps.supportsInjection && caps.supportsOfftake && !caps.onlyOffinje;
